// Issue #40: Sort popover component for course reviews
import React, { useState } from 'react';
import './SortPopover.css';
import { useLanguage } from '../contexts/LanguageContext';
import { useBodyScrollLock } from '../hooks';
import {
  popoverButtonContainerStyle,
  popoverCancelButtonStyle,
  popoverSubmitButtonStyle
} from './shared/PopoverStyles';

function SortPopover({ onClose, onApply, initialSortBy }) {
  const { t } = useLanguage();
  // Issue #279: Single selection only - store as string, not array
  const [selectedSort, setSelectedSort] = useState(initialSortBy || '');

  // Issue #78: Lock background scrolling while the popover is open so the page
  // underneath cannot move or be interacted with via scroll/touch gestures.
  useBodyScrollLock(true);

  // Select a sort option (single selection - clicking same option deselects it)
  const selectSort = (option) => {
    setSelectedSort(prev => prev === option ? '' : option);
  };

  const handleApply = () => {
    onApply(selectedSort);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const isSelected = (option) => selectedSort === option;

  return (
    // Issue #115: Added aria-modal and role for accessibility
    <div className="sort-popover-overlay" onClick={handleCancel} aria-hidden="true">
      <div className="sort-popover" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="sort-popover-title">
        {/* Header */}
        {/* Issue #97: Use same icon as course review main page */}
        <div className="sort-popover-header">
          <span className="sort-icon" aria-hidden="true">📊</span>
          <span className="sort-title" id="sort-popover-title">{t('courseReview.sort.title')}</span>
        </div>

        {/* Content - Single select with radio buttons */}
        <div className="sort-popover-content" role="radiogroup" aria-label={t('courseReview.sort.title')}>
          {/* Sort Options - Single select radio buttons */}
          <button
            type="button"
            className={`sort-option ${isSelected('review_count_desc') ? 'selected' : ''}`}
            onClick={() => selectSort('review_count_desc')}
            role="radio"
            aria-checked={isSelected('review_count_desc')}
          >
            <div className="sort-option-radio" aria-hidden="true">
              {isSelected('review_count_desc') && <div className="sort-option-radio-checked"></div>}
            </div>
            <span className="sort-option-label">{t('courseReview.sort.reviewsDescending')}</span>
          </button>

          {/* Issue #279: Rating ascending (worst to best) sort option */}
          <button
            type="button"
            className={`sort-option ${isSelected('rating_asc') ? 'selected' : ''}`}
            onClick={() => selectSort('rating_asc')}
            role="radio"
            aria-checked={isSelected('rating_asc')}
          >
            <div className="sort-option-radio" aria-hidden="true">
              {isSelected('rating_asc') && <div className="sort-option-radio-checked"></div>}
            </div>
            <span className="sort-option-label">{t('courseReview.sort.ratingAscending')}</span>
          </button>

          {/* Issue #441: Rating descending (best to worst) sort option */}
          <button
            type="button"
            className={`sort-option ${isSelected('rating_desc') ? 'selected' : ''}`}
            onClick={() => selectSort('rating_desc')}
            role="radio"
            aria-checked={isSelected('rating_desc')}
          >
            <div className="sort-option-radio" aria-hidden="true">
              {isSelected('rating_desc') && <div className="sort-option-radio-checked"></div>}
            </div>
            <span className="sort-option-label">{t('courseReview.sort.ratingDescending')}</span>
          </button>

          {/* Issue #136: A-Z sort option */}
          <button
            type="button"
            className={`sort-option ${isSelected('name_asc') ? 'selected' : ''}`}
            onClick={() => selectSort('name_asc')}
            role="radio"
            aria-checked={isSelected('name_asc')}
          >
            <div className="sort-option-radio" aria-hidden="true">
              {isSelected('name_asc') && <div className="sort-option-radio-checked"></div>}
            </div>
            <span className="sort-option-label">{t('courseReview.sort.nameAsc')}</span>
          </button>

          {/* Issue #136: Z-A sort option */}
          <button
            type="button"
            className={`sort-option ${isSelected('name_desc') ? 'selected' : ''}`}
            onClick={() => selectSort('name_desc')}
            role="radio"
            aria-checked={isSelected('name_desc')}
          >
            <div className="sort-option-radio" aria-hidden="true">
              {isSelected('name_desc') && <div className="sort-option-radio-checked"></div>}
            </div>
            <span className="sort-option-label">{t('courseReview.sort.nameDesc')}</span>
          </button>

          {/* Selected Sort Chip - show selected option */}
          {selectedSort && (
            <div className="sort-selected-chips" aria-live="polite">
              <span className="sort-selected-chip">
                {selectedSort === 'review_count_desc' && t('courseReview.sort.reviewsDescending')}
                {selectedSort === 'rating_asc' && t('courseReview.sort.ratingAscending')}
                {selectedSort === 'rating_desc' && t('courseReview.sort.ratingDescending')}
                {selectedSort === 'name_asc' && t('courseReview.sort.nameAsc')}
                {selectedSort === 'name_desc' && t('courseReview.sort.nameDesc')}
              </span>
            </div>
          )}

          {/* Buttons */}
          <div style={popoverButtonContainerStyle}>
            <button
              onClick={handleCancel}
              style={popoverCancelButtonStyle}
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleApply}
              style={popoverSubmitButtonStyle}
            >
              {t('courseReview.filter.apply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SortPopover;
